import { useEffect, useState, useRef } from "react";
import {
  Avatar,
  Card,
  Spinner,
  Alert,
  Button,
  TextInput,
  Label,
  Tooltip,
} from "flowbite-react";
import { useAuth } from "../../contexts/AuthContext";
import { supabase } from "../../lib/supabase";
import type { User } from "@supabase/supabase-js";
import NavbarSidebarLayout from "../../layouts/navbar-sidebar";
import { clearWorkerSignatureCache } from "../../utils/contractDataCache";
import {
  HiExclamationCircle,
  HiUserCircle,
  HiBriefcase,
  HiOfficeBuilding,
  HiTrendingUp,
  HiCheckCircle,
  HiXCircle,
  HiUser,
  HiPencil,
  HiCamera,
} from "react-icons/hi";
import SignaturePad from "../../components/contracts/SignaturePad";

interface WorkerProfile {
  id: number;
  user_id: string;
  created_at: string;
  full_name: string | null;
  email: string | null;
  role_id: number | null;
  profile_pic: string | null;
  signature_path: string | null;
  revenue_goal_month: number | null;
  revenue_goal_daily: number | null;
  manager_id: number | null;
  organization: string | null;
  active: boolean | null;
}

interface Role {
  id: number;
  role_name: string | null;
}

interface Manager {
  id: number;
  full_name: string | null;
}

const BUCKET_NAME = "profilepic";

const ProfilePage = () => {
  const { user } = useAuth();
  const [worker, setWorker] = useState<WorkerProfile | null>(null);
  const [role, setRole] = useState<Role | null>(null);
  const [manager, setManager] = useState<Manager | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [editMode, setEditMode] = useState(false);
  const [editableMonthlyGoal, setEditableMonthlyGoal] = useState("");
  const [editableDailyGoal, setEditableDailyGoal] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadingPicture, setUploadingPicture] = useState(false);
  const [pictureUploadError, setPictureUploadError] = useState<string | null>(
    null
  );
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);

  // Signature state management
  const [showSignaturePad, setShowSignaturePad] = useState(false);
  const [isUploadingSignature, setIsUploadingSignature] = useState(false);
  const [signatureError, setSignatureError] = useState<string | null>(null);

  useEffect(() => {
    const fetchProfileData = async (currentUser: User) => {
      setLoading(true);
      setError(null);
      setSaveError(null);
      setPictureUploadError(null);
      setSignatureError(null);
      setAvatarUrl(null);

      try {
        const { data: workerData, error: workerError } = await supabase
          .from("workers")
          .select("*")
          .eq("user_id", currentUser.id)
          .single();

        if (workerError) {
          if (workerError.code === "PGRST116") {
            setError(
              "Profile not found for this user. Please contact support if you believe this is an error."
            );
          } else {
            throw workerError;
          }
        }

        if (workerData) {
          setWorker(workerData as WorkerProfile);
          setEditableMonthlyGoal(
            workerData.revenue_goal_month?.toString() || ""
          );
          setEditableDailyGoal(workerData.revenue_goal_daily?.toString() || "");

          if (workerData.profile_pic) {
            const { data: signedUrlData, error: signedUrlError } =
              await supabase.storage
                .from(BUCKET_NAME)
                .createSignedUrl(workerData.profile_pic, 3600);
            if (signedUrlError) {
              console.error(
                "Error creating signed URL for existing pic:",
                signedUrlError
              );
            } else {
              setAvatarUrl(signedUrlData.signedUrl);
            }
          }

          if (workerData.role_id) {
            const { data: roleData, error: roleError } = await supabase
              .from("roles")
              .select("id, role_name")
              .eq("id", workerData.role_id)
              .single();
            if (roleError) throw roleError;
            setRole(roleData as Role);
          }

          if (workerData.manager_id) {
            const { data: managerData, error: managerError } = await supabase
              .from("workers")
              .select("id, full_name")
              .eq("id", workerData.manager_id)
              .single();
            if (managerError) throw managerError;
            setManager(managerData as Manager);
          }
        }
      } catch (err) {
        console.error("Error fetching profile data:", err);
        const errorMessage =
          err instanceof Error ? err.message : "An unknown error occurred.";
        setError(`Failed to load profile: ${errorMessage}`);
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      fetchProfileData(user);
    } else {
      setLoading(false);
    }
  }, [user]);

  const handleToggleEditMode = () => {
    setEditMode(!editMode);
    setSaveError(null);
    if (worker) {
      setEditableMonthlyGoal(worker.revenue_goal_month?.toString() || "");
      setEditableDailyGoal(worker.revenue_goal_daily?.toString() || "");
    }
  };

  const handleSaveGoals = async () => {
    if (!user || !worker) {
      setSaveError("User or worker data is not available. Cannot save.");
      return;
    }
    setIsSaving(true);
    setSaveError(null);

    const monthlyGoal = parseFloat(editableMonthlyGoal);
    const dailyGoal = parseFloat(editableDailyGoal);

    if (isNaN(monthlyGoal) || isNaN(dailyGoal)) {
      setSaveError("Revenue goals must be valid numbers.");
      setIsSaving(false);
      return;
    }

    try {
      const { data: rpcData, error: rpcError } = await supabase.rpc(
        "update_my_revenue_goals",
        {
          worker_user_id_to_update: worker.user_id,
          new_monthly_goal: monthlyGoal,
          new_daily_goal: dailyGoal,
        }
      );

      if (rpcError) throw rpcError;

      console.log("RPC Response for goal update:", rpcData);

      setWorker((prevWorker) =>
        prevWorker
          ? {
              ...prevWorker,
              revenue_goal_month: monthlyGoal,
              revenue_goal_daily: dailyGoal,
            }
          : null
      );
      setEditMode(false);
    } catch (err: any) {
      console.error("RAW ERROR saving revenue goals via RPC:", err);
      let detailedMessage = "Failed to save goals. Please try again.";
      if (err && typeof err === "object") {
        if (
          "details" in err &&
          typeof err.details === "string" &&
          err.details
        ) {
          detailedMessage = err.details;
        } else if (
          "message" in err &&
          typeof err.message === "string" &&
          err.message
        ) {
          detailedMessage = err.message;
        } else {
          try {
            detailedMessage = JSON.stringify(err);
          } catch {
            // Fallback
          }
        }
      }
      setSaveError(detailedMessage);
    } finally {
      setIsSaving(false);
    }
  };

  const handleFileTrigger = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelected = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setPictureUploadError(null);
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      if (file.size > 2.5 * 1024 * 1024) {
        setPictureUploadError("File is too large. Maximum size is 2.5MB.");
        return;
      }
      await handlePictureUpload(file);
    }
    event.target.value = "";
  };

  const handlePictureUpload = async (fileToUpload: File) => {
    if (!fileToUpload || !user || !worker) {
      setPictureUploadError("No file selected or user/worker data missing.");
      return;
    }

    setUploadingPicture(true);
    setPictureUploadError(null);

    const fileExt = fileToUpload.name.split(".").pop();
    const fileName = `${user.id}_${Date.now()}.${fileExt}`;
    const filePath = `${user.id}/${fileName}`;

    try {
      const { error: uploadError } = await supabase.storage
        .from(BUCKET_NAME)
        .upload(filePath, fileToUpload, {
          cacheControl: "3600",
          upsert: true,
        });

      if (uploadError) throw uploadError;

      const { error: rpcError } = await supabase.rpc(
        "update_my_profile_pic_url",
        {
          new_profile_pic_url: filePath,
        }
      );

      if (rpcError) throw rpcError;

      const { data: signedUrlData, error: signedUrlError } =
        await supabase.storage
          .from(BUCKET_NAME)
          .createSignedUrl(filePath, 3600);

      if (signedUrlError) {
        console.error(
          "Error creating signed URL after upload:",
          signedUrlError
        );
        setPictureUploadError(
          "File uploaded but could not generate display URL."
        );
      } else {
        setAvatarUrl(signedUrlData.signedUrl);
      }

      setWorker((prevWorker) =>
        prevWorker ? { ...prevWorker, profile_pic: filePath } : null
      );
    } catch (err: any) {
      console.error("Error uploading profile picture:", err);
      let detailedMessage = "Failed to upload profile picture.";
      if (err && typeof err === "object") {
        if (
          "details" in err &&
          typeof err.details === "string" &&
          err.details
        ) {
          detailedMessage = err.details;
        } else if (
          "message" in err &&
          typeof err.message === "string" &&
          err.message
        ) {
          detailedMessage = err.message;
        } else {
          try {
            detailedMessage = JSON.stringify(err);
          } catch {
            /* fallback */
          }
        }
      }
      setPictureUploadError(detailedMessage);
    } finally {
      setUploadingPicture(false);
    }
  };

  const handleSignatureCancel = () => {
    setShowSignaturePad(false);
    setSignatureError(null);
  };

  const handleSignatureSave = async (signaturePath: string, _dataUrl: string) => {
    if (!user || !worker) {
      setSignatureError("User or worker data is not available.");
      return;
    }

    setIsUploadingSignature(true);
    setSignatureError(null);

    try {
      // SignaturePad already uploaded to storage, we just receive the path
      // Update database via RPC
      const { error: rpcError } = await supabase.rpc("update_my_signature", {
        new_signature_path: signaturePath,
      });

      if (rpcError) throw rpcError;

      // Update local state
      setWorker((prevWorker) =>
        prevWorker ? { ...prevWorker, signature_path: signaturePath } : null
      );

      // Clear worker signature cache to ensure fresh signature is loaded next time
      if (user?.id) {
        clearWorkerSignatureCache(user.id);
        console.log("Worker signature cache cleared");
      }

      // Close modal
      setShowSignaturePad(false);
    } catch (err: any) {
      console.error("Error saving signature:", err);
      let detailedMessage = "Failed to save signature.";
      if (err && typeof err === "object") {
        if (
          "details" in err &&
          typeof err.details === "string" &&
          err.details
        ) {
          detailedMessage = err.details;
        } else if (
          "message" in err &&
          typeof err.message === "string" &&
          err.message
        ) {
          detailedMessage = err.message;
        } else {
          try {
            detailedMessage = JSON.stringify(err);
          } catch {
            // fallback
          }
        }
      }
      setSignatureError(detailedMessage);
    } finally {
      setIsUploadingSignature(false);
    }
  };

  if (loading) {
    return (
      <NavbarSidebarLayout pageTitle="User Profile">
        <div className="flex h-full items-center justify-center">
          <Spinner size="xl" aria-label="Loading profile data..." />
        </div>
      </NavbarSidebarLayout>
    );
  }

  if (error) {
    return (
      <NavbarSidebarLayout pageTitle="Error">
        <div className="p-4">
          <Alert color="failure" icon={HiExclamationCircle}>
            <span className="font-medium">Error!</span> {error}
          </Alert>
        </div>
      </NavbarSidebarLayout>
    );
  }

  if (!worker) {
    return (
      <NavbarSidebarLayout pageTitle="Profile Not Found">
        <div className="p-4">
          <Alert color="warning" icon={HiUserCircle}>
            No worker profile found. If you've just registered, your profile
            might still be setting up.
          </Alert>
        </div>
      </NavbarSidebarLayout>
    );
  }

  return (
    <NavbarSidebarLayout pageTitle="User Profile">
      <div className="container mx-auto p-4 md:p-6 lg:p-8">
        <Card className="mx-auto max-w-2xl">
          <div className="flex flex-col items-center pb-10">
            <div className="relative mb-3">
              {avatarUrl ? (
                <Avatar img={avatarUrl} rounded size="xl" bordered />
              ) : worker.profile_pic ? (
                <div className="flex h-24 w-24 items-center justify-center rounded-full ring-2 ring-gray-300 dark:ring-gray-500">
                  <Spinner size="lg" />
                </div>
              ) : (
                <div className="rounded-full p-1 ring-2 ring-gray-300 dark:ring-gray-500">
                  <HiUserCircle className="h-24 w-24 text-gray-400 dark:text-gray-600" />
                </div>
              )}
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileSelected}
                accept="image/png, image/jpeg, image/gif, image/webp"
                className="hidden"
                aria-label="Upload profile picture"
                title="Upload profile picture"
              />
              <Tooltip content="Change Profile Picture">
                <Button
                  onClick={handleFileTrigger}
                  disabled={uploadingPicture}
                  size="sm"
                  className="absolute bottom-0 right-0 rounded-full p-1.5"
                  color="light"
                >
                  {uploadingPicture ? (
                    <Spinner size="xs" />
                  ) : (
                    <HiCamera className="h-5 w-5" />
                  )}
                </Button>
              </Tooltip>
            </div>
            {pictureUploadError && (
              <div className="my-2 w-full max-w-xs">
                <Alert
                  color="failure"
                  icon={HiExclamationCircle}
                  onDismiss={() => setPictureUploadError(null)}
                >
                  <span className="font-medium">Upload Error:</span>{" "}
                  {pictureUploadError}
                </Alert>
              </div>
            )}
            <h5 className="mb-1 text-2xl font-medium text-gray-900 dark:text-white">
              {worker.full_name || "N/A"}
            </h5>
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {worker.email || "Keine E-Mail-Adresse angegeben"}
            </span>

            <div className="mt-4 grid w-full grid-cols-1 gap-4 md:grid-cols-2">
              <InfoItem
                icon={HiBriefcase}
                label="Rolle"
                value={role?.role_name || "Nicht zugewiesen"}
              />
              <InfoItem
                icon={HiOfficeBuilding}
                label="Organisation"
                value={worker.organization || "N/A"}
              />
              {manager && (
                <InfoItem
                  icon={HiUser}
                  label="Manager"
                  value={manager.full_name || "N/A"}
                />
              )}

              {!editMode ? (
                <>
                  <InfoItem
                    icon={HiTrendingUp}
                    label="Monats Umsatz Ziel"
                    value={
                      worker.revenue_goal_month
                        ? `€${worker.revenue_goal_month.toLocaleString()}`
                        : "Nicht festgelegt"
                    }
                  />
                  <InfoItem
                    icon={HiTrendingUp}
                    label="Tägliches Umsatz Ziel"
                    value={
                      worker.revenue_goal_daily
                        ? `€${worker.revenue_goal_daily.toLocaleString()}`
                        : "Nicht festgelegt"
                    }
                  />
                </>
              ) : (
                <>
                  <div>
                    <Label htmlFor="monthlyGoal">Monats Umsatz Ziel (€)</Label>
                    <TextInput
                      id="monthlyGoal"
                      type="number"
                      value={editableMonthlyGoal}
                      onChange={(e) => setEditableMonthlyGoal(e.target.value)}
                      placeholder="z.B. 8000"
                      className="mt-1 sentry-unmask"
                    />
                  </div>
                  <div>
                    <Label htmlFor="dailyGoal">Tägliches Umsatz Ziel (€)</Label>
                    <TextInput
                      id="dailyGoal"
                      type="number"
                      value={editableDailyGoal}
                      onChange={(e) => setEditableDailyGoal(e.target.value)}
                      placeholder="z.B. 500"
                      className="mt-1 sentry-unmask"
                    />
                  </div>
                </>
              )}

              <div className="flex items-center space-x-2 text-sm text-gray-700 dark:text-gray-300 md:col-span-2">
                {worker.active ? (
                  <HiCheckCircle className="h-5 w-5 text-green-500" />
                ) : (
                  <HiXCircle className="h-5 w-5 text-red-500" />
                )}
                <span>{worker.active ? "Aktiver Status" : "Inaktiv"}</span>
              </div>
            </div>

            {saveError && (
              <div className="mt-4 w-full">
                <Alert color="failure" icon={HiExclamationCircle}>
                  <span className="font-medium">Save Error!</span> {saveError}
                </Alert>
              </div>
            )}

            {signatureError && (
              <div className="mt-4 w-full">
                <Alert
                  color="failure"
                  icon={HiExclamationCircle}
                  onDismiss={() => setSignatureError(null)}
                >
                  <span className="font-medium">Signature Error:</span>{" "}
                  {signatureError}
                </Alert>
              </div>
            )}

            <div className="mt-6 flex w-full items-center justify-center space-x-3 lg:mt-8">
              {!editMode ? (
                <>
                  <Button onClick={handleToggleEditMode} size="sm">
                    <HiPencil className="mr-2 h-5 w-5" />
                    Ziele bearbeiten
                  </Button>
                  <Button
                    onClick={() => setShowSignaturePad(true)}
                    disabled={isUploadingSignature}
                    size="sm"
                    color="light"
                  >
                    {isUploadingSignature ? (
                      <Spinner size="sm" className="mr-2" />
                    ) : (
                      <HiPencil className="mr-2 h-5 w-5" />
                    )}
                    {worker.signature_path
                      ? "Unterschrift aktualisieren"
                      : "Unterschrift hinzufügen"}
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    onClick={handleSaveGoals}
                    disabled={isSaving}
                    size="sm"
                    color="success"
                  >
                    {isSaving ? <Spinner size="sm" className="mr-2" /> : null}
                    Speichern
                  </Button>
                  <Button
                    onClick={handleToggleEditMode}
                    disabled={isSaving}
                    color="alternative"
                    size="sm"
                  >
                    Abbrechen
                  </Button>
                </>
              )}
            </div>
          </div>
        </Card>
      </div>

      {/* Signature Pad Modal */}
      {showSignaturePad && (
        <SignaturePad
          onSave={handleSignatureSave}
          onCancel={handleSignatureCancel}
        />
      )}
    </NavbarSidebarLayout>
  );
};

interface InfoItemProps {
  icon: React.ElementType;
  label: string;
  value: string | number | null;
}

const InfoItem: React.FC<InfoItemProps> = ({ icon: Icon, label, value }) => (
  <div className="flex items-center space-x-2 text-sm text-gray-700 dark:text-gray-300">
    <Icon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
    <span>
      <span className="font-semibold">{label}:</span> {value}
    </span>
  </div>
);

export default ProfilePage;
